import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer } from '../components/Toast';
import '../styles/Admincardinaux.css';
import '../styles/Toast.css';
import '../styles/EditCardinal.css';

const EditCardinal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    nomComplet: '',
    titreHonorifique: '',
    dateNaissance: '',
    lieuNaissance: '',
    paysNaissance: '',
    paysService: '',
    continent: 'Europe',
    ordre: 'Cardinal-prêtre',
    nationalite: '',
    languesParlees: [],
    titre: '',
    dateCreation: '',
    creePar: '',
    formation: '',
    specialisation: '',
    biographie: '',
    photo: '',
    papabile: false,
    statutVotant: 'OUI'
  });
  
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [toasts, setToasts] = useState([]);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  // Gestion des toasts
  const addToast = (type, message, duration = 5000) => {
    const id = Date.now();
    setToasts(prevToasts => [...prevToasts, { id, type, message, duration }]);
  };

  const removeToast = (id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };
  
  // Charger les données du cardinal à éditer
  useEffect(() => {
    const fetchCardinal = async () => {
      try {
        const response = await axios.get(`/api/cardinaux/${id}`);
        const cardinal = response.data;
        
        // Formatter les languesParlees pour l'affichage
        const languesString = Array.isArray(cardinal.languesParlees) 
          ? cardinal.languesParlees.join(', ') 
          : '';
        
        // Formatter les dates pour l'input date
        const formatDate = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };
        
        setFormData({
          ...cardinal,
          dateNaissance: formatDate(cardinal.dateNaissance),
          dateCreation: formatDate(cardinal.dateCreation),
          languesParlees: languesString,
          statutVotant: cardinal.statutVotant || 'OUI',
          // Utiliser les valeurs par défaut si manquantes
          continent: cardinal.continent || 'Europe',
          ordre: cardinal.ordre || 'Cardinal-prêtre',
          paysNaissance: cardinal.paysNaissance || cardinal.pays || '',
          paysService: cardinal.paysService || cardinal.paysExercice || cardinal.paysNaissance || ''
        });
        
        // Réinitialiser l'état d'erreur d'image
        setImageError(false);
        
        if (cardinal.photo) {
          setPhotoPreview(cardinal.photo);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement du cardinal:', err);
        addToast('error', `Erreur: ${err.response?.data?.msg || err.message}`);
        setLoading(false);
      }
    };
    
    fetchCardinal();
  }, [id]);
  
  // Validation des champs
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'nom', 'prenom', 'dateNaissance', 'paysNaissance', 
      'paysService', 'continent', 'ordre', 'dateCreation'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'Ce champ est requis';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Effacer l'erreur lorsque l'utilisateur commence à saisir
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleLanguesChange = (e) => {
    setFormData({
      ...formData,
      languesParlees: e.target.value.split(',').map(lang => lang.trim())
    });
  };
  
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      // Créer une prévisualisation
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setImageError(false);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    
    // Valider le formulaire
    if (!validateForm()) {
      addToast('error', 'Veuillez remplir tous les champs requis');
      return;
    }
    
    try {
      // Formater les données
      const cardinalData = {
        ...formData,
        nomComplet: `${formData.prenom} ${formData.nom}`
      };
      
      // Si une nouvelle photo a été sélectionnée, l'uploader d'abord
      if (photoFile) {
        const formData = new FormData();
        formData.append('photo', photoFile);
        formData.append('nom', cardinalData.nom);
        formData.append('prenom', cardinalData.prenom);
        formData.append('id', id); // Pour le nommage du fichier
        
        console.log("Envoi de la photo...");
        
        const uploadResponse = await axios.post('/api/upload/cardinal-photo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log("Réponse upload:", uploadResponse.data);
        
        // Ajouter l'URL de la photo au cardinal avec un timestamp pour éviter le cache
        const timestamp = Date.now();
        cardinalData.photo = `${uploadResponse.data.photoUrl}?v=${timestamp}`;
      }
      
      const response = await axios.put(`/api/cardinaux/${id}`, cardinalData);
      addToast('success', `Cardinal mis à jour avec succès: ${response.data.nomComplet}`);
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/cardinaux/liste');
      }, 2000);
    } catch (err) {
      addToast('error', `Erreur: ${err.response?.data?.msg || err.message}`);
    }
  };
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl) => {
    if (!originalUrl || imageError) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = () => {
    console.log("Erreur de chargement d'image, utilisation de l'image par défaut");
    setImageError(true);
  };
  
  if (loading) return <div className="loading">Chargement...</div>;
  
  return (
    <div className="admin-page">
      <h1>Modifier un Cardinal</h1>
      
      <div className="admin-header-actions">
        <Link to="/admin/cardinaux/liste" className="back-button">← Retour à la liste</Link>
        
        {/* Nouveau bouton pour accéder à la gestion du statut Papabile */}
        <Link to={`/admin/cardinaux/papabile/${id}`} className="papabile-admin-button">
          Gérer le statut Papabile
        </Link>
      </div>
      
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <form onSubmit={handleSubmit} className="cardinal-form">
        <div className="form-section">
          <h2>Informations personnelles</h2>
          
          <div className={`form-group ${errors.prenom && formSubmitted ? 'has-error' : ''}`}>
            <label>Prénom</label>
            <input 
              type="text" 
              name="prenom" 
              value={formData.prenom} 
              onChange={handleChange} 
              required 
              className={errors.prenom && formSubmitted ? 'error-input' : ''}
            />
            {errors.prenom && formSubmitted && <span className="error-message">{errors.prenom}</span>}
          </div>
          
          <div className={`form-group ${errors.nom && formSubmitted ? 'has-error' : ''}`}>
            <label>Nom</label>
            <input 
              type="text" 
              name="nom" 
              value={formData.nom} 
              onChange={handleChange} 
              required 
              className={errors.nom && formSubmitted ? 'error-input' : ''}
            />
            {errors.nom && formSubmitted && <span className="error-message">{errors.nom}</span>}
          </div>
          
          <div className="form-group">
            <label>Titre Honorifique</label>
            <input 
              type="text" 
              name="titreHonorifique" 
              value={formData.titreHonorifique} 
              onChange={handleChange} 
            />
          </div>
          
          <div className={`form-group ${errors.dateNaissance && formSubmitted ? 'has-error' : ''}`}>
            <label>Date de naissance</label>
            <input 
              type="date" 
              name="dateNaissance" 
              value={formData.dateNaissance} 
              onChange={handleChange} 
              required 
              className={errors.dateNaissance && formSubmitted ? 'error-input' : ''}
            />
            {errors.dateNaissance && formSubmitted && <span className="error-message">{errors.dateNaissance}</span>}
          </div>
          
          <div className="form-group">
            <label>Lieu de naissance</label>
            <input 
              type="text" 
              name="lieuNaissance" 
              value={formData.lieuNaissance} 
              onChange={handleChange} 
            />
          </div>
          
          <div className={`form-group ${errors.paysNaissance && formSubmitted ? 'has-error' : ''}`}>
            <label>Pays de naissance</label>
            <input 
              type="text" 
              name="paysNaissance" 
              value={formData.paysNaissance} 
              onChange={handleChange} 
              required 
              className={errors.paysNaissance && formSubmitted ? 'error-input' : ''}
            />
            {errors.paysNaissance && formSubmitted && <span className="error-message">{errors.paysNaissance}</span>}
          </div>
          
          <div className={`form-group ${errors.paysService && formSubmitted ? 'has-error' : ''}`}>
            <label>Pays de service</label>
            <input 
              type="text" 
              name="paysService" 
              value={formData.paysService} 
              onChange={handleChange} 
              required 
              className={errors.paysService && formSubmitted ? 'error-input' : ''}
            />
            {errors.paysService && formSubmitted && <span className="error-message">{errors.paysService}</span>}
          </div>
          
          <div className={`form-group ${errors.continent && formSubmitted ? 'has-error' : ''}`}>
            <label>Continent</label>
            <select
              name="continent"
              value={formData.continent}
              onChange={handleChange}
              required
              className={errors.continent && formSubmitted ? 'error-input' : ''}
            >
              <option value="Afrique">Afrique</option>
              <option value="Amérique">Amérique</option>
              <option value="Asie">Asie</option>
              <option value="Europe">Europe</option>
              <option value="Océanie">Océanie</option>
            </select>
            {errors.continent && formSubmitted && <span className="error-message">{errors.continent}</span>}
          </div>
          
          <div className="form-group">
            <label>Nationalité</label>
            <input 
              type="text" 
              name="nationalite" 
              value={formData.nationalite} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="form-group">
            <label>Langues parlées (séparées par des virgules)</label>
            <input 
              type="text" 
              name="languesParlees" 
              value={formData.languesParlees} 
              onChange={handleLanguesChange} 
            />
          </div>
          
          <div className="form-group">
            <label>Photo actuelle</label>
            {photoPreview ? (
              <div className="current-photo">
                <img 
                  src={getApiImageUrl(formData.photo)} 
                  alt="Photo actuelle" 
                  onError={handleImageError}
                />
              </div>
            ) : (
              <p>Aucune photo</p>
            )}
            
            <label>Nouvelle photo (optionnel)</label>
            <input 
              type="file" 
              name="newPhoto" 
              accept="image/*" 
              onChange={handlePhotoChange} 
            />
            {photoFile && (
              <div className="photo-preview">
                <img src={URL.createObjectURL(photoFile)} alt="Prévisualisation" />
              </div>
            )}
          </div>
        </div>
        
        <div className="form-section">
          <h2>Informations cardinalices</h2>
          
          <div className={`form-group ${errors.ordre && formSubmitted ? 'has-error' : ''}`}>
            <label>Ordre</label>
            <select
              name="ordre"
              value={formData.ordre}
              onChange={handleChange}
              required
              className={errors.ordre && formSubmitted ? 'error-input' : ''}
            >
              <option value="Cardinal-évêque">Cardinal-évêque</option>
              <option value="Cardinal-prêtre">Cardinal-prêtre</option>
              <option value="Cardinal-diacre">Cardinal-diacre</option>
            </select>
            {errors.ordre && formSubmitted && <span className="error-message">{errors.ordre}</span>}
          </div>
          
          <div className="form-group">
            <label>Titre</label>
            <input 
              type="text" 
              name="titre" 
              value={formData.titre} 
              onChange={handleChange} 
            />
          </div>
          
          <div className={`form-group ${errors.dateCreation && formSubmitted ? 'has-error' : ''}`}>
            <label>Date de création comme cardinal</label>
            <input 
              type="date" 
              name="dateCreation" 
              value={formData.dateCreation} 
              onChange={handleChange} 
              required 
              className={errors.dateCreation && formSubmitted ? 'error-input' : ''}
            />
            {errors.dateCreation && formSubmitted && <span className="error-message">{errors.dateCreation}</span>}
          </div>
          
          <div className="form-group">
            <label>Créé par (Pape)</label>
            <input 
              type="text" 
              name="creePar" 
              value={formData.creePar} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="form-group">
            <label>Formation</label>
            <input 
              type="text" 
              name="formation" 
              value={formData.formation} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="form-group">
            <label>Spécialisation</label>
            <input 
              type="text" 
              name="specialisation" 
              value={formData.specialisation} 
              onChange={handleChange} 
            />
          </div>
          
          <div className="form-group">
            <label>Biographie</label>
            <textarea 
              name="biographie" 
              value={formData.biographie} 
              onChange={handleChange} 
              rows="5"
            ></textarea>
          </div>
          
          <div className="form-group radio-group">
            <label>Statut Votant</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="statutVotant"
                  value="OUI"
                  checked={formData.statutVotant === 'OUI'}
                  onChange={handleChange}
                />
                OUI
              </label>
              <label>
                <input
                  type="radio"
                  name="statutVotant"
                  value="NON"
                  checked={formData.statutVotant === 'NON'}
                  onChange={handleChange}
                />
                NON
              </label>
            </div>
          </div>
          
          <div className="form-group checkbox">
            <label>
              <input 
                type="checkbox" 
                name="papabile" 
                checked={formData.papabile} 
                onChange={handleChange} 
              />
              Considéré comme Papabile
            </label>
          </div>
        </div>
        
        <div className="form-actions">
          <button type="submit">Mettre à jour le Cardinal</button>
          <button type="button" className="cancel-button" onClick={() => navigate('/admin/cardinaux/liste')}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCardinal;